import { Link, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { collection, onSnapshot, query, where, limit } from 'firebase/firestore';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  Inbox as InboxIcon,
  Send,
  Users,
  Mail,
  BarChart3,
  Activity,
  Flame,
  ChevronDown,
  LogOut,
  Menu,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth.js';
import { db } from '../lib/firebase.js';
import { Wordmark } from './landing/Section.js';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Whether a given pathname should mark this item active. */
  match: (pathname: string) => boolean;
}

// Top-level nav, kept to a clean five: Dashboard, Inbox, Campaigns, Contacts,
// and a Deliverability group (below). Icons are 16px in the nav.
const PRIMARY: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, match: (p) => p === '/' },
  {
    to: '/inbox',
    label: 'Inbox',
    icon: InboxIcon,
    match: (p) => p.startsWith('/inbox'),
  },
  {
    to: '/campaigns/new',
    label: 'Campaigns',
    icon: Send,
    match: (p) => p.startsWith('/campaigns'),
  },
  {
    to: '/contacts',
    label: 'Contacts',
    icon: Users,
    match: (p) => p.startsWith('/contacts'),
  },
];

// The four deliverability tools live under a single "Deliverability" menu so
// the top-level nav stays uncluttered.
const DELIVERABILITY: NavItem[] = [
  {
    to: '/mailboxes',
    label: 'Mailboxes',
    icon: Mail,
    match: (p) => p.startsWith('/mailboxes'),
  },
  {
    to: '/deliverability/reports',
    label: 'Reports',
    icon: BarChart3,
    match: (p) => p.startsWith('/deliverability/reports'),
  },
  {
    to: '/deliverability/health',
    label: 'Sender Health',
    icon: Activity,
    match: (p) => p.startsWith('/deliverability/health'),
  },
  {
    to: '/deliverability/warmup',
    label: 'Warmup',
    icon: Flame,
    match: (p) => p.startsWith('/deliverability/warmup'),
  },
];

/** Live count of unread inbox threads for the nav badge (INBOX_SPEC §8). */
function useUnreadThreads(tenantId: string | null): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!tenantId) {
      setCount(0);
      return;
    }
    const q = query(
      collection(db, `tenants/${tenantId}/threads`),
      where('unread', '==', true),
      limit(100),
    );
    return onSnapshot(
      q,
      (snap) => setCount(snap.size),
      () => setCount(0),
    );
  }, [tenantId]);
  return count;
}

/** Small pill badge for the live unread count. */
function UnreadBadge({ count }: { count: number }) {
  return (
    <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-brand-700 px-1.5 py-0.5 font-mono text-[11px] font-semibold leading-none text-white tabular-nums">
      {count > 99 ? '99+' : count}
    </span>
  );
}

const NAV_LINK =
  'inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition duration-200 ease-out';
const NAV_ACTIVE =
  'bg-brand-50 text-brand-700 shadow-sm ring-1 ring-inset ring-brand-600/10';
const NAV_IDLE = 'text-slate-600 hover:bg-slate-100 hover:text-slate-900';

export function Layout({ children }: { children: ReactNode }) {
  const { user, tenantId, signOut } = useAuth();
  const loc = useLocation();
  const unread = useUnreadThreads(tenantId);

  const [delivOpen, setDelivOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const delivRef = useRef<HTMLDivElement>(null);
  const acctRef = useRef<HTMLDivElement>(null);

  // Close every menu on navigation.
  useEffect(() => {
    setDelivOpen(false);
    setAcctOpen(false);
    setMobileOpen(false);
  }, [loc.pathname]);

  // Close the desktop dropdowns on an outside click.
  useEffect(() => {
    if (!delivOpen && !acctOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (delivRef.current && !delivRef.current.contains(t)) setDelivOpen(false);
      if (acctRef.current && !acctRef.current.contains(t)) setAcctOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [delivOpen, acctOpen]);

  const delivActive = DELIVERABILITY.some((n) => n.match(loc.pathname));
  const email = user?.email ?? '';
  const initial = email.charAt(0).toUpperCase() || '?';

  return (
    <div className="app-canvas min-h-screen">
      {/* Polished sticky nav: translucent + blurred, hairline bottom border. */}
      <nav className="nav-surface sticky top-0 z-30 border-b border-slate-200/70">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex items-center gap-6">
            <Link to="/" aria-label="RevBase" className="shrink-0">
              <Wordmark />
            </Link>

            {/* Desktop nav */}
            <div className="hidden items-center gap-1 lg:flex">
              {PRIMARY.map((n) => {
                const active = n.match(loc.pathname);
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    className={clsx(NAV_LINK, active ? NAV_ACTIVE : NAV_IDLE)}
                  >
                    <n.icon className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
                    {n.label}
                    {n.to === '/inbox' && unread > 0 && (
                      <UnreadBadge count={unread} />
                    )}
                  </Link>
                );
              })}

              {/* Deliverability dropdown */}
              <div className="relative" ref={delivRef}>
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={delivOpen}
                  onClick={() => setDelivOpen((v) => !v)}
                  className={clsx(
                    NAV_LINK,
                    delivActive || delivOpen ? NAV_ACTIVE : NAV_IDLE,
                  )}
                >
                  <Activity className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
                  Deliverability
                  <ChevronDown
                    className={clsx(
                      'h-4 w-4 transition-transform duration-200',
                      delivOpen && 'rotate-180',
                    )}
                    aria-hidden
                  />
                </button>
                {delivOpen && (
                  <div
                    role="menu"
                    className="absolute left-0 top-full z-40 mt-2 w-60 overflow-hidden rounded-2xl border border-slate-200 bg-white p-1.5 shadow-soft-lg"
                  >
                    {DELIVERABILITY.map((n) => {
                      const active = n.match(loc.pathname);
                      return (
                        <Link
                          key={n.to}
                          to={n.to}
                          role="menuitem"
                          className={clsx(
                            'flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium transition duration-150',
                            active
                              ? 'bg-brand-50 text-brand-700'
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
                          )}
                        >
                          <n.icon className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
                          {n.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Account menu (desktop) */}
            <div className="relative hidden lg:block" ref={acctRef}>
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={acctOpen}
                onClick={() => setAcctOpen((v) => !v)}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white/70 py-1.5 pl-1.5 pr-2.5 text-sm text-slate-600 shadow-sm transition duration-200 hover:border-slate-300 hover:bg-white"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-700 font-display text-xs font-bold text-white">
                  {initial}
                </span>
                <span className="hidden max-w-[14rem] truncate font-mono text-xs text-slate-500 xl:inline">
                  {email}
                </span>
                <ChevronDown
                  className={clsx(
                    'h-4 w-4 transition-transform duration-200',
                    acctOpen && 'rotate-180',
                  )}
                  aria-hidden
                />
              </button>
              {acctOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-40 mt-2 w-64 overflow-hidden rounded-2xl border border-slate-200 bg-white p-1.5 shadow-soft-lg"
                >
                  <div className="border-b border-slate-100 px-3 py-2.5">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                      Signed in as
                    </p>
                    <p className="mt-0.5 truncate font-mono text-xs text-slate-700">
                      {email}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => void signOut()}
                    className="mt-1 flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium text-slate-600 transition duration-150 hover:bg-red-50 hover:text-red-600"
                  >
                    <LogOut className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
                    Sign out
                  </button>
                </div>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              type="button"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white/70 p-2 text-slate-600 shadow-sm transition duration-200 hover:bg-white lg:hidden"
            >
              {mobileOpen ? (
                <X className="h-5 w-5" strokeWidth={2} aria-hidden />
              ) : (
                <Menu className="h-5 w-5" strokeWidth={2} aria-hidden />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileOpen && (
          <div className="border-t border-slate-200/70 bg-white/95 px-4 py-4 lg:hidden">
            <div className="space-y-1">
              {PRIMARY.map((n) => {
                const active = n.match(loc.pathname);
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    className={clsx(
                      'flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition duration-150',
                      active ? NAV_ACTIVE : NAV_IDLE,
                    )}
                  >
                    <n.icon className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
                    {n.label}
                    {n.to === '/inbox' && unread > 0 && (
                      <UnreadBadge count={unread} />
                    )}
                  </Link>
                );
              })}
            </div>

            <p className="mt-4 px-3 text-[11px] font-semibold uppercase tracking-widest text-slate-400">
              Deliverability
            </p>
            <div className="mt-1 space-y-1">
              {DELIVERABILITY.map((n) => {
                const active = n.match(loc.pathname);
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    className={clsx(
                      'flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition duration-150',
                      active ? NAV_ACTIVE : NAV_IDLE,
                    )}
                  >
                    <n.icon className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
                    {n.label}
                  </Link>
                );
              })}
            </div>

            <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-200/70 pt-4">
              <div className="flex min-w-0 items-center gap-2">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-700 font-display text-xs font-bold text-white">
                  {initial}
                </span>
                <span className="truncate font-mono text-xs text-slate-500">
                  {email}
                </span>
              </div>
              <button
                type="button"
                onClick={() => void signOut()}
                className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 shadow-sm transition duration-200 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
              >
                <LogOut className="h-4 w-4" strokeWidth={2} aria-hidden />
                Sign out
              </button>
            </div>
          </div>
        )}
      </nav>

      <main className="mx-auto max-w-7xl px-4 py-7 sm:px-6 sm:py-8">
        {children}
      </main>
    </div>
  );
}
